import { useMemo } from 'react';
import { AxiosResponse } from 'axios';
import useAdvancedSearchData from 'src/serverProxy/useAdvancedSearchData';
import AdvancedSearchParser from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/AdvancedSearchParser';
import { AdvancedSearchResponse } from 'src/components/BeaconV2Components/utils/AdvancedSearchParser/types';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { useSplineComparisonDateRange } from 'src/utils/chartUtils';

interface PromotionData {
  retailSales_sum_value: number;
  retailSales_sum_count: number;
  retailSalesPrev_sum_value: number;
  retailSalesPrev_sum_count: number;
  retailSalesDelta_sum_value: number;
  retailSalesDelta_sum_count: number;
  retailPricePrev_avg_value: number;
  retailPricePrev_avg_count: number;
  retailPrice_avg_value: number;
  retailPrice_avg_count: number;
  retailPriceDelta_avg_value: number;
  retailPriceDelta_avg_count: number;
  averagePromotionDiscount_computed_value: number;
  averagePromotionDiscount_computed_count: number;
  stacklineSku_cardinality_value: number;
  stacklineSku_cardinality_count: number;
  weekId: string;
  promoType: string;
  name: string;
}
export interface WeeklyPromotionData {
  [weekId: string]: PromotionData;
}
/**
 * Fetches weekly promotion data for the retail sales, wholesale sales, units sold, retail price spline charts.
 * Used to display promotion metrics alongside the regular weekly spline chart metrics.
 */
export const useWeeklyPromotionDataForSpline = (shouldPerformFetch = false) => {
  const { comparisonStartWeek, comparisonEndWeek } = useSplineComparisonDateRange();
  const { isProductEntity } = useProductEntity();
  const groupByField = isProductEntity ? 'weekId,promoType' : 'weekId';
  const { data, isLoading } = useAdvancedSearchData({
    requestId: 'promotionMetrics',
    primaryGenericAdvancedSearchArgs: {
      shouldPerformFetch
    },
    comparisonGenericAdvancedSearchArgs: {
      shouldPerformFetch
    },
    requestBuilder(builder, isComparison) {
      if (isComparison) {
        builder.replaceConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek);
        builder.modifyAggregation((aggregationBuilder) => {
          aggregationBuilder.replaceConditionRangeFilter('weekId', comparisonStartWeek, comparisonEndWeek);
          return aggregationBuilder;
        });
      }
      builder.addConditionRangeFilter('retailSales', 1);
      return builder;
    },
    returnRawResponse: true,
    indexName: 'promotions',
    fields: [
      { name: 'retailSales' },
      { name: 'promotionPercentChange' },
      { name: 'retailSalesDelta' },
      { name: 'retailPricePrev' },
      { name: 'retailPrice' },
      { name: 'averagePromotionDiscount' },
      { name: 'stacklineSku' }
    ],
    groupByField
  });

  const weeklyData = useMemo(() => {
    if (!isLoading && data?.length) {
      const parser = new AdvancedSearchParser(data as AxiosResponse<AdvancedSearchResponse[]>[]);
      return parser.parseAggregations((aggregations) => {
        const dataByWeek = aggregations?.by_weekId?.reduce((acc, current) => {
          if (!(current?.fieldId in acc)) {
            acc[current?.fieldId] = { ...(current?.additionalValues ?? {}), ...(current?.additionalMetaData ?? {}) };
          }

          return acc;
        }, {});
        return dataByWeek;
      }) as WeeklyPromotionData;
    }
    return null;
  }, [data, isLoading]);
  return weeklyData;
};
